// no default React import needed with react-jsx
import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
} from '@mui/material'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

import { adminApi } from '../lib/sdkClient'

export default function AnalyticsPage() {
  const [timeframe, setTimeframe] = useState('7d')
  const [usageData, setUsageData] = useState<any[]>([])
  const [trackTypeData, setTrackTypeData] = useState<any[]>([])
  const [storageData, setStorageData] = useState<any[]>([])
  // const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleTimeframeChange = (newTimeframe: string) => {
    setTimeframe(newTimeframe)
  }

  const loadAnalytics = async (tf: string) => {
    // setLoading(true)
    try {
      const { data: usage } = await adminApi.adminAnalyticsUsageArtrackAdminAnalyticsUsageGet(tf as any)
      setUsageData((usage as any)?.series || [])
      setTrackTypeData((usage as any)?.trackTypes || [])
      setStorageData((usage as any)?.storage || [])
      setError(null)
    } catch (e: any) {
      setError(e.message || 'Failed to load analytics')
      setUsageData([])
      setTrackTypeData([])
      setStorageData([])
    } finally {
      // setLoading(false)
    }
  }

  useEffect(() => {
    loadAnalytics(timeframe)
  }, [timeframe])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Analytics Dashboard
        </Typography>
        
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Timeframe</InputLabel>
          <Select
            value={timeframe}
            onChange={(e) => handleTimeframeChange(e.target.value)}
            label="Timeframe"
          >
            <MenuItem value="1d">Last 24 Hours</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      <Grid container spacing={3}>
        {/* Usage Trends */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardHeader title="Usage Trends" />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={usageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="users" stroke="#8884d8" name="Active Users" />
                  <Line type="monotone" dataKey="tracks" stroke="#82ca9d" name="New Tracks" />
                  <Line type="monotone" dataKey="waypoints" stroke="#ffc658" name="New Waypoints" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Track Types Distribution */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardHeader title="Track Types" />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={trackTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {trackTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Storage Growth */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader title="Storage Usage (GB)" />
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={storageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="storage" stroke="#8884d8" fill="#8884d8" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* User Activity */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader title="Daily Active Users" />
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={usageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="users" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="API Performance Metrics" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      245ms
                    </Typography>
                    <Typography color="textSecondary">
                      Avg Response Time
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      99.8%
                    </Typography>
                    <Typography color="textSecondary">
                      Uptime
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      0.2%
                    </Typography>
                    <Typography color="textSecondary">
                      Error Rate
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      1,234
                    </Typography>
                    <Typography color="textSecondary">
                      Requests/Hour
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}